import React, { useState, useEffect } from 'react';
import { InstallerBuildPanel } from './InstallerBuildPanel';
import { useNetworkHelpers } from './useNetworkHelpers';
import SectionedDialog from './SectionedDialog';

/** Build installer for an EXISTING node.
 *
 * Opens from NodeDetail. Operator picks a flavor (docker / bare-metal),
 * an optional Anthropic API key / namespace, and
 * (optionally) refines the node's helper inventory via the picker.
 * On submit, POSTs to /api/nodes/{nodeId}/build-installer which:
 *   1. Fetches the node's full standalone credentials + persisted
 *      helper_configs from hub.
 *   2. Replays helper_configs through aveq-admin/helper_processors.py
 *      to derive env_stamps (KEY=VAL pairs baked into the recipient's
 *      .env). NO operator UI input for helper-specific configs — they
 *      were captured at Add Node time and persist on the hub-side
 *      node_prop (node-helper-config-persistence slug).
 *   3. Spawns the same _run_build_installer asyncio task the Add Node
 *      standalone-substrate flow uses, returning {installer_task_id}.
 *   4. The InstallerBuildPanel then polls for completion + offers the
 *      download link.
 *
 * Helpers picker (build-installer-helper-picker slug):
 *   - "Available" comes from the network root's NodeService inventory
 *     via useNetworkHelpers — same source AddNodeModal uses.
 *   - Default-checked set is the node's existing helpers_included CSV
 *     fetched from GET /api/nodes/{id}/helpers-included.
 *   - On submit, the chosen array (or '__none__' sentinel for an empty
 *     pick) goes into the POST body's `helpers_included` field. The
 *     BFF forwards it to build-installer.sh's --helpers flag which
 *     stamps the hub-side node_prop. Untouched picker = field omitted
 *     = hub stamp unchanged (token-rotation rebuild semantic).
 *
 * What this modal does NOT collect:
 *   - Helper-specific configs (sensor_picos, etc.) — those live on the
 *     node from Add Node time and replay automatically. To CHANGE one,
 *     use Add Node's re-mint path (or the deferred Edit Helpers slug).
 *
 * Failure modes worth surfacing to the operator:
 *   404 — node not found.
 *   409 — ambiguous app-identity (multiple member rows match
 *         '<node-name>-%' — node was re-minted in the past without
 *         cleanup; operator must delete the obsolete member(s) in PG).
 *   412 — node has no app-identity (was provisioned without
 *         --app-identity; not a standalone-substrate node). */
export default function BuildInstallerModal({ nodeId, nodeName, networkId, onClose }) {
  const [flavor, setFlavor] = useState('docker');
  // installer-target-arch slug: docker recipients are per-arch; bare-metal
  // is arch-neutral (jar + pip wheels resolve at install time). Default is
  // User-Agent-driven — Apple Silicon Mac → arm64, everything else →
  // amd64. Operator can override via the radio.
  const [bundleArch, setBundleArch] = useState('amd64');
  useEffect(() => {
    let cancelled = false;
    // navigator.userAgentData is Chrome/Edge (+ newer browsers). Safari
    // has no high-entropy arch hint AND its UA on Apple Silicon Mac still
    // says "Macintosh; Intel Mac OS X 10_15_7" (Apple intentionally
    // obscures the chip), so the heuristic can't catch Safari users —
    // they'll see amd64 and click the radio if they want arm64. That's
    // acceptable; the UA hint is best-effort, not authoritative.
    const uad = navigator.userAgentData;
    if (uad && typeof uad.getHighEntropyValues === 'function') {
      uad.getHighEntropyValues(['architecture']).then((hints) => {
        if (cancelled) return;
        if (hints && hints.architecture === 'arm') {
          setBundleArch('arm64');
        }
      }).catch(() => { /* swallow; default stays amd64 */ });
    }
    return () => { cancelled = true; };
  }, []);

  // bundles-picker-availability: the picker must offer ONLY flavor/arch combos that are
  // actually built on prod (/srv/aveq/bundles), so it can't promise a bundle that
  // doesn't exist (the bug that broke arm64 builds). /api/bundles lists them for this
  // deploy's version. null = loading; [] = none found (dev, or transient) → don't lock
  // the operator out (treat as "unknown", show everything).
  const [availBundles, setAvailBundles] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetch('/api/bundles')
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => { if (!cancelled) setAvailBundles((body && body.available) || []); })
      .catch(() => { if (!cancelled) setAvailBundles([]); });
    return () => { cancelled = true; };
  }, []);
  const bundlesRestrict = Array.isArray(availBundles) && availBundles.length > 0;
  const flavorsAvail = new Set((availBundles || []).map((b) => b.flavor));
  const dockerArches = new Set(
    (availBundles || []).filter((b) => b.flavor === 'docker').map((b) => b.arch));
  const flavorAvail = (f) => !bundlesRestrict || flavorsAvail.has(f);
  const archAvail = (a) => !bundlesRestrict || dockerArches.has(a);
  // Correct the default selection to something actually built, once the list lands —
  // overrides the User-Agent arm64 guess when no arm64 bundle exists.
  useEffect(() => {
    if (!bundlesRestrict) return;
    if (!flavorsAvail.has(flavor)) { setFlavor(flavorsAvail.has('docker') ? 'docker' : 'bare-metal'); return; }
    if (flavor === 'docker' && !dockerArches.has(bundleArch)) {
      setBundleArch(dockerArches.has('amd64') ? 'amd64' : (Array.from(dockerArches)[0] || 'amd64'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availBundles, flavor]);

  const [anthropicKey, setAnthropicKey] = useState('');
  const [namespace, setNamespace] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [installerTaskId, setInstallerTaskId] = useState(null);
  // Build status reported up by InstallerBuildPanel. The "Done" button
  // stays disabled until this leaves 'pending', so the operator can't
  // dismiss the modal while the .run is still being built (and the
  // download link doesn't exist yet).
  const [buildStatus, setBuildStatus] = useState('pending');
  // admin-sectioned-dialogs: active category pane.
  const [activeCat, setActiveCat] = useState('flavor');

  // Helpers picker — available inventory from network root, defaulted
  // to the node's currently-stamped helpers_included. The first
  // operator interaction flips `helpersDirty`; until then, submit
  // omits the field entirely so the hub stamp stays intact.
  const { helpers: availableHelpers, loading: helpersLoading, error: helpersError } =
    useNetworkHelpers(networkId);
  const [selectedHelpers, setSelectedHelpers] = useState(null);
  const [helpersDirty, setHelpersDirty] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/nodes/${encodeURIComponent(String(nodeId))}/helpers-included`)
      .then((r) => r.ok ? r.json() : null)
      .then((body) => {
        if (cancelled) return;
        const list = body && Array.isArray(body.helpers_list) ? body.helpers_list : [];
        setSelectedHelpers(list);
      })
      .catch(() => {
        if (!cancelled) setSelectedHelpers([]);
      });
    return () => { cancelled = true; };
  }, [nodeId]);

  const toggleHelper = (module) => {
    setSelectedHelpers((prev) => {
      const set = new Set(prev || []);
      if (set.has(module)) set.delete(module); else set.add(module);
      return Array.from(set);
    });
    setHelpersDirty(true);
  };
  const selectAllHelpers = () => {
    setSelectedHelpers((availableHelpers || []).map((h) => h.module));
    setHelpersDirty(true);
  };
  const selectNoneHelpers = () => {
    setSelectedHelpers([]);
    setHelpersDirty(true);
  };

  // referenced-helpers slug: a SEPARATE picker for helpers this node may CALL
  // across the mesh (not run locally — that's the Helpers picker above).
  // Options = function sets referenceable from the network (global, or any
  // in-network station). Selection POSTs as `referenced_helpers`; the BFF forwards
  // it to build-installer.sh --referenced, which stamps the node_prop and bakes
  // the resolved specs into the bundle. Starts empty (no current-selection
  // defaulting in v1).
  const [availableReferenced, setAvailableReferenced] = useState([]);
  const [referencedLoading, setReferencedLoading] = useState(true);
  const [selectedReferenced, setSelectedReferenced] = useState([]);
  const [referencedDirty, setReferencedDirty] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setReferencedLoading(true);
    fetch(`/api/networks/${encodeURIComponent(String(networkId))}/referenced-functions`)
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        if (cancelled) return;
        const list = body && Array.isArray(body.referenced_functions) ? body.referenced_functions : [];
        setAvailableReferenced(list);
      })
      .catch(() => { if (!cancelled) setAvailableReferenced([]); })
      .finally(() => { if (!cancelled) setReferencedLoading(false); });
    return () => { cancelled = true; };
  }, [networkId]);

  const toggleReferenced = (name) => {
    setSelectedReferenced((prev) => {
      const set = new Set(prev || []);
      if (set.has(name)) set.delete(name); else set.add(name);
      return Array.from(set);
    });
    setReferencedDirty(true);
  };

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    // bundles-picker-availability: belt-and-suspenders — the radios already hide
    // unbuilt combos, but guard the submit too so a stale selection can't POST a
    // build for a bundle that doesn't exist on prod.
    if (bundlesRestrict) {
      const ok = flavor === 'docker' ? dockerArches.has(bundleArch) : flavorsAvail.has(flavor);
      if (!ok) {
        setError(`No ${flavor}${flavor === 'docker' ? '/' + bundleArch : ''} bundle is built for this version. Pick an available option, or build it on prod (build-standalone-bundle-on-prod.sh${flavor === 'docker' && bundleArch === 'arm64' ? ' --arm64' : ''}).`);
        return;
      }
    }
    setSubmitting(true);
    setError(null);
    setInstallerTaskId(null);
    setBuildStatus('pending');
    try {
      const body = { flavor };
      // installer-target-arch slug: docker `.run` carries a per-arch
      // qlink image, so the recipient's CPU arch needs to be known at
      // build time. bare-metal is arch-neutral; skip the field.
      if (flavor === 'docker') body.bundle_arch = bundleArch;
      if (anthropicKey.trim()) body.anthropic_api_key = anthropicKey.trim();
      if (namespace.trim()) body.qlink_namespace = namespace.trim();
      if (helpersDirty) {
        // Confirm the empty-set case — easy to misclick + the
        // implication (bare relay; no chat-surface helpers) deserves
        // an explicit yes. Skip the confirm if the node was already
        // stamped as __none__ and the operator left it that way.
        if (!selectedHelpers || selectedHelpers.length === 0) {
          const ok = window.confirm(
            'You unchecked every helper. The recipient will install as a bare relay (helpers_included will be set to __none__ on the hub). Continue?'
          );
          if (!ok) {
            setSubmitting(false);
            return;
          }
          body.helpers_included = '__none__';
        } else {
          body.helpers_included = selectedHelpers;
        }
      }
      // referenced-helpers slug: only include when the operator touched the
      // picker; empty array is a valid choice (no referenced helpers).
      if (referencedDirty) {
        body.referenced_helpers = selectedReferenced || [];
      }
      const r = await fetch(`/api/nodes/${nodeId}/build-installer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const text = await r.text();
      let parsed = null;
      try { parsed = JSON.parse(text); } catch (_) {}
      if (!r.ok) {
        // Surface 404 / 409 / 412 with a helpful hint inline; everything
        // else falls through to the parsed error / raw text.
        const hint = (
          r.status === 409 ? ' (ambiguous app-identity — node was re-minted; clean up extra member rows in PG first)' :
          r.status === 412 ? ' (node has no app-identity — was it provisioned via the standalone flow / `provision-node.sh --app-identity`?)' :
          r.status === 404 ? ' (node not found)' :
          ''
        );
        throw new Error(
          (parsed && (parsed.message || parsed.error) || text || `HTTP ${r.status}`) + hint
        );
      }
      setInstallerTaskId(parsed.installer_task_id);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setSubmitting(false);
    }
  };

  const renderFlavor = () => (
    <>
            <fieldset style={fieldsetStyle}>
              <legend style={{ fontSize: '0.85rem', color: '#444', padding: '0 6px' }}>Flavor</legend>
              <label style={radioRowStyle}>
                <input type="radio" name="flavor" value="docker"
                       checked={flavor === 'docker'}
                       disabled={!flavorAvail('docker')}
                       onChange={(e) => setFlavor(e.target.value)} />
                <span><strong>Docker</strong> — recipient has Docker runtime{!flavorAvail('docker') && ' — not built for this version'}</span>
              </label>
              <label style={radioRowStyle}>
                <input type="radio" name="flavor" value="bare-metal"
                       checked={flavor === 'bare-metal'}
                       disabled={!flavorAvail('bare-metal')}
                       onChange={(e) => setFlavor(e.target.value)} />
                <span><strong>Bare-metal</strong> — recipient has Java 21 + Python 3 (no Docker){!flavorAvail('bare-metal') && ' — not built for this version'}</span>
              </label>
            </fieldset>
    </>
  );

  // installer-target-arch slug: only docker recipients need arch selection.
  // Bare-metal jar + pip wheels resolve at install time, so the category hides.
  const renderArch = () => (
              <fieldset style={fieldsetStyle}>
                <legend style={{ fontSize: '0.85rem', color: '#444', padding: '0 6px' }}>Target architecture</legend>
                <label style={radioRowStyle}>
                  <input type="radio" name="bundle_arch" value="amd64"
                         checked={bundleArch === 'amd64'}
                         disabled={!archAvail('amd64')}
                         onChange={(e) => setBundleArch(e.target.value)} />
                  <span><strong>amd64</strong> — Linux servers (default for non-Mac targets){!archAvail('amd64') && ' — not built'}</span>
                </label>
                <label style={radioRowStyle}>
                  <input type="radio" name="bundle_arch" value="arm64"
                         checked={bundleArch === 'arm64'}
                         disabled={!archAvail('arm64')}
                         onChange={(e) => setBundleArch(e.target.value)} />
                  <span><strong>arm64</strong> — Apple Silicon Mac{!archAvail('arm64') && ' — not built (use bare-metal, which is arch-neutral, or ask the operator to build --arm64)'}</span>
                </label>
              </fieldset>
  );

  const renderHelpers = () => (
            <fieldset style={fieldsetStyle}>
              <legend style={{ fontSize: '0.85rem', color: '#444', padding: '0 6px' }}>Helpers</legend>
              {helpersLoading || selectedHelpers === null ? (
                <div style={{ fontSize: '0.8rem', color: '#888', padding: '4px 2px' }}>
                  Loading helpers…
                </div>
              ) : helpersError ? (
                <div style={{ fontSize: '0.8rem', color: '#c00', padding: '4px 2px' }}>
                  Couldn't load inventory: {helpersError}. Submit without changes to keep the node's existing helpers_included.
                </div>
              ) : (availableHelpers || []).length === 0 ? (
                <div style={{ fontSize: '0.8rem', color: '#888', padding: '4px 2px' }}>
                  No helpers available from this network's root. The recipient will install as a bare relay.
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 6, fontSize: '0.75rem' }}>
                    <button type="button" onClick={selectAllHelpers}
                            style={pickerShortcutStyle}>select all</button>
                    <button type="button" onClick={selectNoneHelpers}
                            style={pickerShortcutStyle}>select none</button>
                    <span style={{ color: '#999', marginLeft: 'auto' }}>
                      {(selectedHelpers || []).length} / {(availableHelpers || []).length} selected
                      {helpersDirty ? '' : ' (default — change to override)'}
                    </span>
                  </div>
                  <div style={{ maxHeight: 200, overflow: 'auto', padding: '2px 4px' }}>
                    {(availableHelpers || []).map((h) => {
                      const checked = (selectedHelpers || []).includes(h.module);
                      return (
                        <label key={h.module} style={{
                          display: 'flex', alignItems: 'flex-start', gap: 6,
                          fontSize: '0.8rem', marginBottom: 3, cursor: 'pointer',
                        }}>
                          <input type="checkbox" checked={checked}
                                 onChange={() => toggleHelper(h.module)}
                                 style={{ marginTop: 3 }} />
                          <div>
                            <code>{h.module}</code>
                            {h.display_name && h.display_name !== h.module && (
                              <span style={{ color: '#666' }}> — {h.display_name}</span>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
              <small style={{ color: '#888', fontSize: '0.72rem', display: 'block', marginTop: 6 }}>
                Helpers dropped in the recipient's <code>EXTRA_ENDPOINT_DIR</code>
                (custom <code>.py</code> files) always load regardless of this list.
              </small>
            </fieldset>
  );

  // referenced-helpers slug: helpers this node may CALL across the mesh (vs the
  // Helpers picker, which it RUNS).
  const renderReferenced = () => (
            <fieldset style={fieldsetStyle}>
              <legend style={{ fontSize: '0.85rem', color: '#444', padding: '0 6px' }}>Referenced helpers</legend>
              {referencedLoading ? (
                <div style={{ fontSize: '0.8rem', color: '#888', padding: '4px 2px' }}>
                  Loading referenceable functions…
                </div>
              ) : (availableReferenced || []).length === 0 ? (
                <div style={{ fontSize: '0.8rem', color: '#888', padding: '4px 2px' }}>
                  Nothing referenceable from this network (no global or in-network helpers exposed). Leave blank.
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 6, fontSize: '0.75rem' }}>
                    <span style={{ color: '#999', marginLeft: 'auto' }}>
                      {(selectedReferenced || []).length} / {(availableReferenced || []).length} selected
                    </span>
                  </div>
                  <div style={{ maxHeight: 200, overflow: 'auto', padding: '2px 4px' }}>
                    {(availableReferenced || []).map((fs) => {
                      const checked = (selectedReferenced || []).includes(fs.name);
                      return (
                        <label key={fs.name} style={{
                          display: 'flex', alignItems: 'flex-start', gap: 6,
                          fontSize: '0.8rem', marginBottom: 3, cursor: 'pointer',
                        }}>
                          <input type="checkbox" checked={checked}
                                 onChange={() => toggleReferenced(fs.name)}
                                 style={{ marginTop: 3 }} />
                          <div>
                            <code>{fs.name}</code>
                            <span style={{ color: '#999', fontSize: '0.7rem' }}> · {fs.source}</span>
                            {fs.description && (
                              <span style={{ color: '#666' }}> — {fs.description}</span>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
              <small style={{ color: '#888', fontSize: '0.72rem', display: 'block', marginTop: 6 }}>
                These run on OTHER nodes; their specs are baked into the bundle so helpers you
                author here can call them via <code>aveq_link.helper_invoke</code>.
              </small>
            </fieldset>
  );

  const renderAdvanced = () => (
    <>
            <label style={labelStyle}>
              <span>Anthropic API key <small style={{ color: '#888' }}>(optional)</small></span>
              <input value={anthropicKey} onChange={(e) => setAnthropicKey(e.target.value)}
                     placeholder="sk-ant-..." style={inputStyle} type="password" />
              <small style={{ color: '#888', fontSize: '0.75rem' }}>
                Baked into the recipient's <code>.env</code>. Only needed if any of
                this node's scoped helpers call Claude (Tobin sentiment, agent-style).
              </small>
            </label>

            <label style={labelStyle}>
              <span>QLINK namespace <small style={{ color: '#888' }}>(optional)</small></span>
              <input value={namespace} onChange={(e) => setNamespace(e.target.value)}
                     placeholder="qlink" style={inputStyle} />
              <small style={{ color: '#888', fontSize: '0.75rem' }}>
                Override the recipient's routing namespace. Default <code>qlink</code>
                shares the route with all other deployed qlinks; pick something
                distinct (e.g. acme) for an isolated route.
              </small>
            </label>
    </>
  );

  const categories = [
    { id: 'flavor', label: 'Flavor', description: `Packages a fresh .run for ${nodeName || `node ${nodeId}`} using its existing credentials — no re-mint.`, render: renderFlavor },
    { id: 'arch', label: 'Architecture', description: 'Target CPU architecture for the docker recipient.', applicable: flavor === 'docker', render: renderArch },
    { id: 'helpers', label: 'Helpers', description: 'Refine which helpers the node hosts. Leave untouched to keep the current set.', render: renderHelpers },
    { id: 'referenced', label: 'Referenced helpers', description: 'Helpers on OTHER nodes this node may call across the mesh.', render: renderReferenced },
    { id: 'advanced', label: 'Advanced', description: 'Optional Anthropic API key and routing namespace.', render: renderAdvanced },
  ];

  const resultNode = installerTaskId ? (
    <InstallerBuildPanel taskId={installerTaskId} onStatusChange={setBuildStatus} />
  ) : null;

  const resultFooter = buildStatus === 'pending' ? (
    <>
      <button type="button" onClick={onClose} style={buttonSecondaryStyle}>Cancel</button>
      <button type="button" disabled style={buttonDisabledStyle}>Building…</button>
    </>
  ) : (
    <button type="button" onClick={onClose} style={buttonPrimaryStyle}>Done</button>
  );

  return (
    <SectionedDialog
      title="Build installer"
      categories={categories}
      activeId={activeCat}
      onSelect={setActiveCat}
      primaryLabel="Build installer"
      submittingLabel="Starting build…"
      submitting={submitting}
      onPrimary={submit}
      onCancel={onClose}
      error={error}
      result={resultNode}
      resultFooter={resultFooter}
    />
  );
}

const labelStyle = {
  display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12,
  fontSize: '0.85rem',
};
const inputStyle = {
  padding: '6px 8px', fontSize: '0.9rem', border: '1px solid #ccc', borderRadius: 4,
};
const fieldsetStyle = {
  border: '1px solid #ddd', borderRadius: 4, padding: '8px 12px 4px',
  marginBottom: 12,
};
const radioRowStyle = {
  display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6,
  fontSize: '0.85rem', cursor: 'pointer',
};
const buttonPrimaryStyle = {
  padding: '8px 16px', background: '#5fa67a', color: '#fff', border: 'none',
  borderRadius: 4, fontSize: '0.85rem', cursor: 'pointer',
};
const buttonSecondaryStyle = {
  padding: '8px 16px', background: 'transparent', color: '#5fa67a',
  border: '1px solid #5fa67a', borderRadius: 4, fontSize: '0.85rem', cursor: 'pointer',
};
const buttonDisabledStyle = {
  padding: '8px 16px', background: '#b9d4c4', color: '#fff', border: 'none',
  borderRadius: 4, fontSize: '0.85rem', cursor: 'not-allowed',
};
const pickerShortcutStyle = {
  padding: '2px 8px', background: '#f0f0f0', border: '1px solid #ccc',
  borderRadius: 3, fontSize: '0.72rem', color: '#444', cursor: 'pointer',
};
